import { TonApiClient } from '@ton-api/client'
import { OpenedContract, Sender } from '@ton/core'
import { TonClient } from '@ton/ton'
import { useTonWallet } from '@tonconnect/ui-react'
import { BoxCollectionV0 } from '@voteboxio/core'
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useContactAddresses } from '../hooks/useContactAddresses'
import { useInitializeContracts } from '../hooks/useInitializeContracts'
import { useInitializeTonApiClient } from '../hooks/useInitializeTonApiClient'
import { useUserProfile } from '../hooks/useUserProfile'
import { useReferralKey } from '../hooks/useReferralKey'
import { onTokenExpired } from '../shared/utils/apiClient'
import { getAuthToken } from '../shared/utils/authToken'
import { UserResponseDto } from '../types/generated'

type AppContextType = {
  language: string
  client: TonClient | undefined
  boxCollection: OpenedContract<BoxCollectionV0.BoxCollectionV0> | undefined
  sender: Sender | undefined
  wallet: string | null
  tonApiClient: TonApiClient | null
  contractsAddresses: ReturnType<typeof useContactAddresses> | undefined
  basePath: string
  referralKey: string | null
  setReferralKey: (key: string | null) => void
  authenticated: boolean
  setAuthenticated: (value: boolean) => void
  /** True when user is authenticated AND wallet is connected - safe to make auth requests */
  canFetchUserData: boolean
  userProfile: UserResponseDto | undefined
  isLoadingProfile: boolean
  isLoadingReferralKey: boolean
}

const contextValue: AppContextType = {
  language: '',
  client: undefined,
  boxCollection: undefined,
  sender: undefined,
  wallet: null,
  tonApiClient: null,
  contractsAddresses: undefined,
  basePath: '/',
  referralKey: null,
  setReferralKey: () => {},
  authenticated: false,
  setAuthenticated: () => {},
  canFetchUserData: false,
  userProfile: undefined,
  isLoadingProfile: false,
  isLoadingReferralKey: false,
}

export const AppContext = createContext<AppContextType>(contextValue)

export const AppContextProvider: FC<
  PropsWithChildren & { language: string }
> = ({ children, language }) => {
  const contractsData = useInitializeContracts()
  const tonApiClient = useInitializeTonApiClient()
  const contractsAddresses = useContactAddresses()
  const wallet = useTonWallet()
  const [authenticated, setAuthenticated] = useState<boolean>(
    Boolean(getAuthToken()),
  )
  // Derived state: safe to make authenticated requests only when both conditions are met
  const canFetchUserData = authenticated && !!wallet?.account.address

  // Load user profile
  const { data: userProfile, isLoading: isLoadingProfile } = useUserProfile(
    wallet?.account.address,
    canFetchUserData,
  )

  // Manage referral key (auto-syncs from profile)
  const {
    referralKey,
    setReferralKey,
    isLoading: isLoadingReferralKey,
  } = useReferralKey(userProfile, isLoadingProfile)

  const basePath = process.env.PUBLIC_PATH || '/'

  // Subscribe to token expiration events
  useEffect(() => {
    const unsubscribe = onTokenExpired(() => {
      setAuthenticated(false)
    })
    return unsubscribe
  }, [])

  return (
    <AppContext.Provider
      value={{
        language,
        client: contractsData?.client,
        wallet: wallet?.account.address ?? null,
        sender: contractsData?.sender,
        boxCollection: contractsData?.boxCollection,
        tonApiClient,
        contractsAddresses,
        basePath,
        referralKey,
        setReferralKey,
        authenticated,
        canFetchUserData,
        setAuthenticated,
        userProfile,
        isLoadingProfile,
        isLoadingReferralKey,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => useContext(AppContext)
